import { calculateDistanceMiles, isValidGeoPoint, type GeoPoint } from "../location/ukGeo";
import { getPublicLocationAreaCode, getPublicLocationCode } from "../location/memberLocation";

export type WhatsOnFilter = 'all' | 'free' | 'low-cost' | 'this-week' | 'school-holidays';
export type WhatsOnDistanceMiles = 5 | 10 | 15 | 20 | 30 | 40 | null;

export type EventRecordLike = {
  id: string;
  title: string;
  description: string | null;
  location: string | null;
  event_date: string;
  is_free: boolean;
  cost?: string | null;
  is_published?: boolean;
  submitted_by?: string | null;
  created_at?: string | null;
};

export type NormalizedEventRecord<T extends EventRecordLike = EventRecordLike> = T & {
  description: string;
  source_name: string | null;
  locality_tags: string[];
  relevance_score: number;
  distance_miles: number | null;
  is_local_match: boolean;
  coordinates: GeoPoint | null;
};

export type AdminReviewEventLike = Pick<EventRecordLike, "event_date" | "is_free" | "cost" | "submitted_by"> & {
  is_published?: boolean | null;
  source_name?: string | null;
  locality_tags?: string[] | null;
  url?: string | null;
  image_url?: string | null;
};

const LOCALITY_MARKER = /\[kindred-local:([^\]]*)\]/i;
const SOURCE_MARKER = /\[kindred-source:([^\]]+)\]/i;
const GEO_MARKER = /\[kindred-geo:([+-]?\d+(?:\.\d+)?),([+-]?\d+(?:\.\d+)?)\]/i;
const LOCALITY_DELIMITERS = /[\n,|]/;
const LOW_COST_KEYWORDS = [
  'low cost',
  'donation',
  'suggested donation',
  'pay what you can',
  'pay-what-you-can',
  'voluntary contribution',
  'affordable',
];

export const TRUSTED_WHATS_ON_SOURCE_NAMES = [
  "Cheshire West and Chester Libraries",
  "Forestry England",
  "National Railway Museum",
  "Science and Industry Museum",
  "Science and Media Museum",
] as const;

export const APPROX_LOCAL_EVENT_RADIUS_MILES = 40;
export const WHATS_ON_DISTANCE_OPTIONS: Array<{ value: WhatsOnDistanceMiles; label: string }> = [
  { value: null, label: 'All distances' },
  { value: 5, label: '5 mi' },
  { value: 10, label: '10 mi' },
  { value: 15, label: '15 mi' },
  { value: 20, label: '20 mi' },
  { value: 30, label: '30 mi' },
  { value: 40, label: '40 mi' },
];

const normalizeCompactToken = (value: string) => value.toLowerCase().trim().replace(/[^a-z0-9]+/g, '');

const TRUSTED_WHATS_ON_SOURCE_TOKENS = new Set(
  TRUSTED_WHATS_ON_SOURCE_NAMES.map((sourceName) => normalizeCompactToken(sourceName)),
);

const addToken = (target: Set<string>, value: string) => {
  const compact = normalizeCompactToken(value);
  if (compact) {
    target.add(compact);
  }
};

export const splitLocalityTags = (value: string | string[] | null | undefined): string[] => {
  const rawItems = Array.isArray(value) ? value : (value ?? '').split(LOCALITY_DELIMITERS);
  const tokens = new Set<string>();

  rawItems.forEach((item) => {
    const trimmed = item.trim();
    if (!trimmed) return;

    addToken(tokens, trimmed);

    trimmed
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(Boolean)
      .forEach((part) => addToken(tokens, part));
  });

  return Array.from(tokens);
};

export const formatLocalityTags = (tags: string[] | null | undefined) => (tags ?? []).join(', ');

export const extractEventSourceName = (description: string | null | undefined) =>
  (description ?? '').match(SOURCE_MARKER)?.[1]?.trim() ?? null;

export const extractEventLocalityTags = (description: string | null | undefined) =>
  splitLocalityTags((description ?? '').match(LOCALITY_MARKER)?.[1] ?? '');

export const extractEventCoordinates = (description: string | null | undefined): GeoPoint | null => {
  const matched = (description ?? '').match(GEO_MARKER);
  if (!matched) {
    return null;
  }

  const latitude = Number.parseFloat(matched[1] ?? '');
  const longitude = Number.parseFloat(matched[2] ?? '');
  return isValidGeoPoint({ latitude, longitude }) ? { latitude, longitude } : null;
};

export const stripEventMeta = (description: string | null | undefined) =>
  (description ?? '')
    .replace(/\n*\[kindred-local:[^\]]*\]\s*/gi, '\n')
    .replace(/\n*\[kindred-source:[^\]]+\]\s*/gi, '\n')
    .replace(/\n*\[kindred-geo:[^\]]+\]\s*/gi, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

export const buildEventDescription = (
  description: string | null | undefined,
  options?: { sourceName?: string | null; localityTags?: string[] | null; coordinates?: GeoPoint | null }
) => {
  const cleanDescription = stripEventMeta(description);
  const metaParts: string[] = [];
  const sourceName = options?.sourceName?.trim();
  const localityTags = splitLocalityTags(options?.localityTags ?? []);
  const coordinates = options?.coordinates;

  if (sourceName) {
    metaParts.push(`[kindred-source:${sourceName}]`);
  }

  if (localityTags.length > 0) {
    metaParts.push(`[kindred-local:${localityTags.join(',')}]`);
  }

  if (isValidGeoPoint(coordinates)) {
    metaParts.push(`[kindred-geo:${coordinates.latitude.toFixed(5)},${coordinates.longitude.toFixed(5)}]`);
  }

  return [cleanDescription, ...metaParts].filter(Boolean).join('\n\n').trim();
};

const buildUserLocalityTokens = (
  postcode: string | null | undefined,
  countryCode?: string | null,
) => {
  if (!postcode) return { full: '', outward: '', area: '' };

  const compact = normalizeCompactToken(postcode);
  const outward = normalizeCompactToken(getPublicLocationCode(postcode, countryCode) ?? '');
  const area = normalizeCompactToken(getPublicLocationAreaCode(postcode, countryCode) ?? '');

  return { full: compact, outward, area };
};

const buildEventLocalityTokenSet = (event: Pick<EventRecordLike, 'location' | 'description'>) => {
  const tokens = new Set<string>();
  splitLocalityTags(event.location ?? '').forEach((item) => addToken(tokens, item));
  extractEventLocalityTags(event.description).forEach((item) => addToken(tokens, item));
  return tokens;
};

export const scoreEventForPostcode = (
  event: Pick<EventRecordLike, 'location' | 'description'>,
  postcode: string | null | undefined,
  countryCode?: string | null,
) => {
  const { full, outward, area } = buildUserLocalityTokens(postcode, countryCode);
  if (!full && !outward && !area) return 0;

  const tokens = buildEventLocalityTokenSet(event);
  let score = 0;

  if (full && tokens.has(full)) score = Math.max(score, 120);
  if (outward && tokens.has(outward)) score = Math.max(score, 100);
  if (area && tokens.has(area)) score = Math.max(score, 35);

  return score;
};

export const normalizeEventRecord = <T extends EventRecordLike>(
  event: T,
  options?: { postcode?: string | null; countryCode?: string | null; userCoordinates?: GeoPoint | null }
): NormalizedEventRecord<T> => {
  const description = stripEventMeta(event.description);
  const source_name = extractEventSourceName(event.description);
  const locality_tags = extractEventLocalityTags(event.description);
  const eventCoordinates = extractEventCoordinates(event.description);
  const distance_miles =
    options?.userCoordinates && eventCoordinates
      ? calculateDistanceMiles(options.userCoordinates, eventCoordinates)
      : null;
  const postcodeScore = scoreEventForPostcode(
    {
      location: event.location,
      description: event.description,
    },
    options?.postcode,
    options?.countryCode,
  );
  const localDistanceScore =
    distance_miles !== null && distance_miles <= APPROX_LOCAL_EVENT_RADIUS_MILES
      ? Math.max(0, 240 - distance_miles)
      : 0;
  const nearbyDistanceScore =
    distance_miles !== null && distance_miles <= APPROX_LOCAL_EVENT_RADIUS_MILES * 2
      ? Math.max(0, 70 - distance_miles / 2)
      : 0;
  const is_local_match = distance_miles !== null && distance_miles <= APPROX_LOCAL_EVENT_RADIUS_MILES;

  return {
    ...event,
    description,
    source_name,
    locality_tags,
    relevance_score: Math.max(postcodeScore, localDistanceScore, nearbyDistanceScore),
    distance_miles,
    is_local_match,
    coordinates: eventCoordinates,
  };
};

export const normalizeEventRecords = <T extends EventRecordLike>(
  events: T[],
  options?: { postcode?: string | null; countryCode?: string | null; userCoordinates?: GeoPoint | null },
) => events.map((event) => normalizeEventRecord(event, options));

export const isUpcomingEvent = (eventDate: string, now = new Date()) => new Date(eventDate).getTime() >= now.getTime();

export const isEventLowCost = (event: Pick<EventRecordLike, 'is_free' | 'cost'>) => {
  if (event.is_free) return false;

  const cost = event.cost?.trim();
  if (!cost) return false;

  const normalized = cost.toLowerCase();
  if (LOW_COST_KEYWORDS.some((keyword) => normalized.includes(keyword))) {
    return true;
  }

  const currencyMatches = Array.from(normalized.matchAll(/£\s*(\d+(?:\.\d+)?)/g)).map((match) => Number(match[1]));
  if (currencyMatches.length > 0) {
    return Math.min(...currencyMatches) <= 15;
  }

  const numericMatches = Array.from(normalized.matchAll(/\b(\d+(?:\.\d+)?)\b/g)).map((match) => Number(match[1]));
  if (numericMatches.length > 0 && /(per|child|family|ticket|adult|pp)/.test(normalized)) {
    return Math.min(...numericMatches) <= 15;
  }

  return false;
};

export const isImportedEventDraft = (event: AdminReviewEventLike) =>
  event.is_published !== true && !event.submitted_by && Boolean(event.source_name || event.url);

export const isMemberSubmittedEventDraft = (event: AdminReviewEventLike) =>
  event.is_published !== true && Boolean(event.submitted_by);

export const isTrustedWhatsOnSource = (sourceName: string | null | undefined) =>
  Boolean(sourceName && TRUSTED_WHATS_ON_SOURCE_TOKENS.has(normalizeCompactToken(sourceName)));

export const isTrustedImportedEventDraft = (event: AdminReviewEventLike) =>
  isImportedEventDraft(event) && isTrustedWhatsOnSource(event.source_name);

export const isTrustedReadyEventDraft = (event: AdminReviewEventLike) =>
  isTrustedImportedEventDraft(event) &&
  Boolean(event.url) &&
  Boolean(event.image_url) &&
  Boolean(event.locality_tags?.length) &&
  isUpcomingEvent(event.event_date) &&
  (event.is_free || isEventLowCost(event));

export const needsAdminEventAttention = (event: AdminReviewEventLike) =>
  event.is_published !== true && !isMemberSubmittedEventDraft(event) && !isTrustedReadyEventDraft(event);

export const filterEventsForView = <T extends EventRecordLike>(
  events: T[],
  filter: WhatsOnFilter,
  schoolHolidays: [Date, Date][]
) =>
  events.filter((event) => {
    if (filter === 'free') return event.is_free;
    if (filter === 'low-cost') return isEventLowCost(event);
    if (filter === 'this-week') {
      const nextWeek = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
      return new Date(event.event_date) <= nextWeek;
    }
    if (filter === 'school-holidays') {
      const date = new Date(event.event_date);
      return schoolHolidays.some(([start, end]) => date >= start && date <= end);
    }
    return true;
  });

export const filterEventsByDistance = <T extends { distance_miles?: number | null }>(
  events: T[],
  maxDistanceMiles: WhatsOnDistanceMiles,
) => {
  if (maxDistanceMiles === null) {
    return events;
  }

  return events.filter((event) => event.distance_miles !== null && event.distance_miles !== undefined && event.distance_miles <= maxDistanceMiles);
};

const compareEventOrder = <T extends EventRecordLike>(left: NormalizedEventRecord<T>, right: NormalizedEventRecord<T>) => {
  const dateDiff = new Date(left.event_date).getTime() - new Date(right.event_date).getTime();
  if (dateDiff !== 0) {
    return dateDiff;
  }

  if (left.distance_miles !== null && right.distance_miles !== null && left.distance_miles !== right.distance_miles) {
    return left.distance_miles - right.distance_miles;
  }

  if (left.distance_miles === null && right.distance_miles !== null) {
    return 1;
  }

  if (left.distance_miles !== null && right.distance_miles === null) {
    return -1;
  }

  if (right.relevance_score !== left.relevance_score) {
    return right.relevance_score - left.relevance_score;
  }

  if (left.is_local_match !== right.is_local_match) {
    return left.is_local_match ? -1 : 1;
  }

  return left.title.localeCompare(right.title);
};

export const orderEventsForMember = <T extends EventRecordLike>(
  events: T[],
  location: { postcode?: string | null; countryCode?: string | null } | null | undefined,
  userId?: string | null,
  userCoordinates?: GeoPoint | null,
) => {
  const normalized = normalizeEventRecords(events, {
    postcode: location?.postcode,
    countryCode: location?.countryCode,
    userCoordinates,
  });
  const published = normalized
    .filter((event) => event.is_published !== false && isUpcomingEvent(event.event_date))
    .sort(compareEventOrder);
  const drafts = normalized
    .filter((event) => event.is_published === false && (!userId || event.submitted_by === userId) && isUpcomingEvent(event.event_date))
    .sort(compareEventOrder);

  return [...published, ...drafts];
};
